import MlmJob from '~/tenants/mlm/model/MlmJob';
import { FIRST_VERSION_TIMESTAMP, ZodVersionedMetadata } from '~/lib/zod';
import { DisplayData, TenantJobLine } from '~/lib/model';
import Big from 'big.js';
import MlmHeadshotConfig, { MlmHeadshotType } from '~/tenants/mlm/performable/headshot/MlmHeadshotConfig';
import { DeliverableType } from '~common/model/Deliverable';
import { PersonaType } from '~/tenants/common/TenantPersona';

export const MLM_HEADSHOT_PEOPLE: Record<number, TMoney> = {
  1: '160.00',
  2: '245.00',
  3: '330.00',
  4: '415.00',
  5: '500.00',
  6: '585.00',
  7: '670.00',
  8: '755.00',
  9: '840.00',
  10: '925.00',
};

export default class MlmHeadshotJob extends MlmJob<typeof MlmHeadshotConfig> {
  get performable() {
    return MlmHeadshotConfig;
  }

  get configurable() {
    return true;
  }

  get hidden() {
    return true;
  }

  get estimate() {
    return this.metadata.type === MlmHeadshotType.LOCATION;
  }

  get media() {
    return PersonaType.PROVIDER;
  }

  get deliverable() {
    return DeliverableType.PHOTO;
  }

  onsite() {
    if (this.metadata.type === MlmHeadshotType.LOCATION) {
      return 40;
    }

    return 30;
  }

  defaultValue(): ZodVersionedMetadata<(typeof MlmHeadshotConfig)['schema']> {
    return {
      version: FIRST_VERSION_TIMESTAMP,
      type: MlmHeadshotType.STUDIO,
    };
  }

  info(): DisplayData[] {
    const info = super.info();

    if (this.metadata.notes) {
      info.push({
        name: 'Notes',
        value: this.metadata.notes,
      });
    }

    if (this.metadata.type === MlmHeadshotType.LOCATION) {
      info.push({
        name: 'Location',
        value: 'On Location',
      });
    } else {
      info.push({
        name: 'Location',
        value: 'MLM Studio',
      });
    }

    if (this.metadata.parking) {
      info.push({
        name: 'Parking',
        value: this.metadata.parking,
      });
    }

    if (this.metadata.people) {
      info.push({
        name: 'People',
        value: this.metadata.people.toString(),
      });
    }

    if (this.metadata.group) {
      info.push({
        name: 'Type',
        value: 'Group Shot',
      });
    }

    return info;
  }

  revenueLines(): TenantJobLine[] {
    const lines = super.revenueLines();

    if (this.metadata.people) {
      lines.push({
        id: 'people',
        discountable: true,
        description: `People (${this.metadata.people})`,
        amount: new Big(MLM_HEADSHOT_PEOPLE[this.metadata.people]),
      });
    } else if (this.metadata.group) {
      lines.push({
        id: 'single',
        discountable: true,
        description: 'Group Shot',
        amount: new Big(125),
      });
    } else {
      lines.push({
        id: 'single',
        discountable: true,
        description: 'Single Person Headshot',
        amount: new Big(125),
      });
    }

    return lines;
  }
}
